/**
 * Event broadcasted on root scope when the browser hash has changed.
 *
 * The event will contain the new hash and old hash as parameters.
 *
 * @type {String}
 */
const HASH_CHANGE_EVENT = 'hashUtils:hashChanged';

/**
 * Event broadcasted on root scope when the AngularJS path has changed.
 *
 * The event will contain the new and old path as parameters.
 *
 * @type {string}
 */
const PATH_CHANGE_EVENT = 'hashUtils:pathChanged';

/**
 * This service contains utilities related to the management of hash changes in AngularJS.
 *
 * We don't use AngularJS's $location as it wants to manage the hash by itself and doesn't allow for others to change
 * it. This means that a page with AngularJS widgets and normal anchor links won't work properly. By removing $location,
 * AngularJS does not manage the URL hash anymore. Related issue: https://github.com/angular/angular.js/issues/5529
 *
 * We could use HTML5 mode but that requires a lot of setting up in production and development environments.
 *
 * There are two different terms used here:
 * - `hash` is the string after the first # in the URL, without the leading #. It can be anything.
 *   Example: http://domain.example/blaa/#foo-bar-baz (hash here is 'foo-bar-baz').
 * - `path` is the AngularJS style path contained inside the hash. If the hash begins with a /, it is treated as a path.
 *   Example: http://domain.example/blaa/#/foo/bar/baz (path here is '/foo/bar/baz').
 *   If the hash does not belong with a /, the path is set to null.
 *   Example: http://domain.example/blaa/#foo-bar/baz (path here is null).
 */
class HashService {
  constructor() {
    'ngInject';

    // Currently active hash or null if no hash
    this._current_hash = null;

    // Currently active path or null if no path
    this._current_path = null;
  }

  /**
   * Get current hash without the preceding '#'
   * @return {String}
   */
  getHash() {
    return window.location.hash.substr(1);
  }

  /**
   * Check if current hash is referring to an AngularJS path (#/path)
   * @return {boolean}
   */
  isHashPath() {
    return this._isPath(this.getHash());
  }

  /**
   * If current hash refers to a path, return the path. Otherwise returns null.
   * @return {?String}
   */
  getPath() {
    if (this.isHashPath()) {
      return this.getHash();
    }
    else {
      return null;
    }
  }

  /**
   * Set the given AngularJS path as new path in the hash.
   * @param {String} path
   * @param {boolean} replace If false (default), a new history entry is added for the new path. If true, the current
   *   history entry is replace so that no new history is created.
   */
  setPath(path, replace=false) {
    if (!replace) {
      window.location.hash = path;
    }
    else {
      const base_url = window.location.href.split('#')[0];
      window.location.replace(`${base_url}#${path}`);
    }
  }

  /**
   * Execute given callback when hash has changed. Callback gets the new hash and old hash as arguments.
   * @param scope
   * @param {function(String, String)} callback
   */
  onHashChange(scope, callback) {
    scope.$on(HASH_CHANGE_EVENT, (e, new_hash, old_hash) => callback(new_hash, old_hash));
  }

  /**
   * Execute given callback when AngularJS path has changed. Callback gets the new path and old path as arguments.
   * @param scope
   * @param {function(String, String)} callback
   */
  onPathChange(scope, callback) {
    scope.$on(PATH_CHANGE_EVENT, (e, new_path, old_path) => callback(new_path, old_path));
  }

  // Is hash a valid path?
  _isPath(hash) {
    return hash.startsWith('/');
  }
}

const mod = angular.module('app.common.hashService', [])

.service('HashService', HashService);

/**
 * React to changes in browser hash by broadcasting new hash on all scopes. This way all components can react to hash
 * changes accordingly.
 */
function reactToHashChanges($rootScope, $timeout, HashService) {
  'ngInject';

  window.addEventListener('hashchange', () => {
    const new_hash = HashService.getHash();
    const old_hash = HashService._current_hash;
    HashService._current_hash = new_hash;

    $timeout(() => {
      $rootScope.$broadcast(HASH_CHANGE_EVENT, new_hash, old_hash);
    });
  });
}
reactToHashChanges.imports = [mod.name];

/**
 * React to changes in hash path by broadcasting new path on all scopes. This way all components can react to path
 * changes accordingly. If new path is not a valid path, null is set instead.
 */
function reactToPathChanges($rootScope, HashService) {
  'ngInject';

  $rootScope.$on(HASH_CHANGE_EVENT, () => {
    const new_path = HashService.getPath();
    const old_path = HashService._current_path;

    if (new_path !== old_path) {
      HashService._current_path = new_path;
      $rootScope.$broadcast(PATH_CHANGE_EVENT, new_path, old_path);
    }
  });
}
reactToPathChanges.imports = [mod.name];

export {reactToHashChanges, reactToPathChanges};
export default mod.name;
